import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import {
  History, Download, FileText, RefreshCw, ChevronDown, ChevronUp, Search,
  Repeat, AlertTriangle, CheckCircle, RotateCcw, Link2, Loader2, Hash,
} from 'lucide-react';

const STATUS_STYLE = {
  open: 'bg-red-100 text-red-800 border-red-200',
  fixed: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  verified: 'bg-green-100 text-green-800 border-green-200',
  reopened: 'bg-amber-100 text-amber-800 border-amber-200',
  regressed: 'bg-orange-100 text-orange-800 border-orange-200',
  closed: 'bg-gray-100 text-gray-700 border-gray-200',
};

const AdminRegressionLog = () => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filterArea, setFilterArea] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [repeatsOnly, setRepeatsOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(null);
  const [jobInputs, setJobInputs] = useState({});

  const attachJobIds = async (id) => {
    const val = (jobInputs[id] || '').trim();
    if (!val) return;
    try {
      const res = await axiosInstance.put(`/admin/regressions/${id}/job-ids`, { jobIds: val, mode: 'append' });
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, jobIds: res.data.jobIds } : it)));
      setJobInputs((prev) => ({ ...prev, [id]: '' }));
      setMessage({ type: 'success', text: `Job ID(s) attached to ${id}.` });
    } catch (e) {
      setMessage({ type: 'error', text: 'Could not attach Job ID.' });
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterArea) params.set('area', filterArea);
      if (filterStatus) params.set('status', filterStatus);
      if (repeatsOnly) params.set('repeatsOnly', 'true');
      if (search) params.set('search', search);
      const q = params.toString() ? `?${params.toString()}` : '';
      const res = await axiosInstance.get(`/admin/regressions${q}`);
      setItems(res.data.regressions || []);
      setSummary(res.data.summary || null);
    } catch (e) {
      setMessage({ type: 'error', text: 'Could not load regression history.' });
    } finally {
      setLoading(false);
    }
  }, [filterArea, filterStatus, repeatsOnly, search]);

  useEffect(() => { fetchData(); }, [filterArea, filterStatus, repeatsOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (path, filename) => {
    setBusy(path);
    try {
      const res = await axiosInstance.get(path, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setMessage({ type: 'error', text: 'Export failed.' });
    } finally {
      setBusy('');
    }
  };

  const resync = async () => {
    setBusy('resync');
    try {
      const res = await axiosInstance.post('/admin/regressions/resync');
      setMessage({ type: 'success', text: `Re-synced from project records (${res.data.count} entries).` });
      await fetchData();
    } catch (e) {
      setMessage({ type: 'error', text: 'Re-sync failed.' });
    } finally {
      setBusy('');
    }
  };

  const areas = summary?.areas || [];

  return (
    <div data-testid="admin-regression-log">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <History className="h-6 w-6 text-emerald-600" /> Regression History
          </h2>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Auto-backfilled historical issue / regression / remediation register, built from the project's own records
            (REGRESSION_REGISTER, PROJECT_IMPACT_LOG, REGRESSION_IMPACT_REPORT). Separate from the manual Issue Log and the
            automatic Error Log. Export for Emergent Support evidence.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => download('/admin/regressions/export.csv', 'find2day_regression_history.csv')}
            disabled={busy === '/admin/regressions/export.csv'}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
            data-testid="regression-export-csv">
            <Download className="h-4 w-4" /> CSV
          </button>
          <button onClick={() => download('/admin/regressions/export.md', 'find2day_regression_report.md')}
            disabled={busy === '/admin/regressions/export.md'}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
            data-testid="regression-export-report">
            <FileText className="h-4 w-4" /> Support Report
          </button>
          <button onClick={resync} disabled={busy === 'resync'}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-60"
            data-testid="regression-resync">
            {busy === 'resync' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Re-sync
          </button>
        </div>
      </div>

      {message && (
        <div className={`mb-3 text-sm px-3 py-2 rounded-lg ${message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          {message.text}
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="regression-summary">
          {[
            { label: 'Total tracked', value: summary.total },
            { label: 'Repeats / regressions', value: summary.repeats },
            { label: 'Total fix rounds', value: summary.totalFixRounds },
            { label: 'Needed prod redeploy', value: summary.redeployRequiredCount },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm">
              <p className="text-2xl font-bold text-gray-900">{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative">
          <Search className="h-4 w-4 text-gray-400 absolute left-3 top-2.5" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchData()}
            placeholder="Search id, title, area, notes…"
            className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-64" data-testid="regression-search" />
        </div>
        <select value={filterArea} onChange={(e) => setFilterArea(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="regression-filter-area">
          <option value="">All areas</option>
          {areas.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="regression-filter-status">
          <option value="">All statuses</option>
          {['open', 'fixed', 'verified', 'reopened', 'regressed', 'closed'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="inline-flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
          <input type="checkbox" checked={repeatsOnly} onChange={(e) => setRepeatsOnly(e.target.checked)} data-testid="regression-repeats-only" />
          <Repeat className="h-4 w-4 text-orange-500" /> Repeats only
        </label>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 py-6">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-400 py-6" data-testid="regression-empty">No entries match.</p>
      ) : (
        <div className="overflow-x-auto bg-white rounded-xl border border-gray-100 shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">ID</th>
                <th className="text-left px-3 py-2 font-medium">Title</th>
                <th className="text-left px-3 py-2 font-medium">Area</th>
                <th className="text-left px-3 py-2 font-medium">First reported</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Repeat?</th>
                <th className="text-left px-3 py-2 font-medium">Rounds</th>
                <th className="text-left px-3 py-2 font-medium">Redeploy</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <React.Fragment key={i.id}>
                  <tr className="border-t border-gray-100 hover:bg-gray-50" data-testid={`regression-row-${i.id}`}>
                    <td className="px-3 py-2 font-mono font-semibold text-gray-900">{i.id}</td>
                    <td className="px-3 py-2 text-gray-800 max-w-md">{i.title}</td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{i.area}</td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{i.firstReported}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full border ${STATUS_STYLE[i.status] || STATUS_STYLE.closed}`}>{i.status}</span>
                    </td>
                    <td className="px-3 py-2">
                      {i.isRepeat ? (
                        <span className="inline-flex items-center gap-1 text-orange-600 text-xs font-medium" title={`Repeat of ${i.repeatOf}`}>
                          <Repeat className="h-3.5 w-3.5" /> {i.repeatOf}
                        </span>
                      ) : <span className="text-gray-400 text-xs">—</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{i.roundsCount}</td>
                    <td className="px-3 py-2">{i.productionRedeployRequired ? <span className="text-amber-600 text-xs font-medium">Yes</span> : <span className="text-gray-400 text-xs">No</span>}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => setExpandedId(expandedId === i.id ? null : i.id)} className="text-gray-500 hover:text-gray-800" data-testid={`regression-expand-${i.id}`}>
                        {expandedId === i.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                    </td>
                  </tr>
                  {expandedId === i.id && (
                    <tr className="bg-gray-50 border-t border-gray-100">
                      <td colSpan={9} className="px-4 py-3">
                        <div className="space-y-3">
                          {i.group && (
                            <p className="text-xs text-orange-700 flex items-center gap-1.5">
                              <AlertTriangle className="h-3.5 w-3.5" /> Repeated-failure group: <strong>{i.group}</strong>
                            </p>
                          )}
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1">Fix / redeploy rounds</p>
                            <ol className="space-y-1.5">
                              {(i.rounds || []).map((r, n) => (
                                <li key={n} className="text-xs text-gray-700 flex gap-2">
                                  <span className="font-mono text-gray-400">{r.date}</span>
                                  <span className="flex-1">
                                    {r.change}
                                    <span className="block text-gray-500 mt-0.5">
                                      verify: {r.verification || '—'} · redeploy: {r.redeployRequired ? 'Yes' : 'No'} · prod-verified: {r.productionVerified ? 'Yes' : 'No'}
                                    </span>
                                  </span>
                                </li>
                              ))}
                            </ol>
                          </div>
                          {i.reworkNotes && (
                            <p className="text-xs text-gray-700"><span className="font-semibold">Rework notes:</span> {i.reworkNotes}</p>
                          )}
                          {i.evidence?.length > 0 && (
                            <p className="text-xs text-gray-600 flex items-start gap-1.5 flex-wrap">
                              <Link2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                              {i.evidence.map((e, n) => <span key={n} className="font-mono bg-white border border-gray-200 rounded px-1.5 py-0.5">{e}</span>)}
                            </p>
                          )}
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                              <Hash className="h-3.5 w-3.5" /> Emergent Job ID(s)
                            </p>
                            <div className="flex items-center gap-2 flex-wrap">
                              {(i.jobIds || []).length > 0 ? (
                                i.jobIds.map((j, n) => (
                                  <span key={n} className="text-xs font-mono bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-1.5 py-0.5" data-testid={`regression-jobid-${i.id}-${n}`}>{j}</span>
                                ))
                              ) : (
                                <span className="text-xs text-gray-400">none yet</span>
                              )}
                              <input
                                value={jobInputs[i.id] || ''}
                                onChange={(e) => setJobInputs((p) => ({ ...p, [i.id]: e.target.value }))}
                                onKeyDown={(e) => e.key === 'Enter' && attachJobIds(i.id)}
                                placeholder="paste Job ID(s), comma-separated"
                                className="text-xs border border-gray-300 rounded px-2 py-1 w-64"
                                data-testid={`regression-jobid-input-${i.id}`}
                              />
                              <button onClick={() => attachJobIds(i.id)}
                                className="text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                                data-testid={`regression-jobid-attach-${i.id}`}>
                                Attach
                              </button>
                            </div>
                          </div>
                          <p className="text-[11px] text-gray-400">Source: {i.source} · {i.origin === 'manual' ? 'manual entry' : 'backfilled'}</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminRegressionLog;
